import { forwardRef } from "react";
import { Mountain } from "lucide-react";
import { fmtValue } from "./reviewStats";

const DAY_MS = 86400000;
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function MiniHeat({ dayMap, year }) {
  const max = Math.max(0, ...Object.values(dayMap).map(Math.abs));
  const start = new Date(year, 0, 1);
  start.setDate(start.getDate() - ((start.getDay() + 6) % 7));
  const weeks = [];
  for (let w = new Date(start); w <= new Date(year, 11, 31); w = new Date(w.getTime() + 7 * DAY_MS)) {
    weeks.push(Array.from({ length: 7 }, (_, i) => new Date(w.getFullYear(), w.getMonth(), w.getDate() + i)));
  }
  const color = (d) => {
    if (d.getFullYear() !== year) return "transparent";
    const v = dayMap[iso(d)];
    if (v == null) return "rgba(255,255,255,0.07)";
    const k = max ? Math.abs(v) / max : 0;
    const a = 0.35 + k * 0.65;
    return v > 0 ? `rgba(16,185,129,${a})` : v < 0 ? `rgba(244,63,94,${a})` : "rgba(148,163,184,0.6)";
  };
  return (
    <div className="flex gap-[2px]">
      {weeks.map((wk, i) => (
        <div key={i} className="flex flex-col gap-[2px]">
          {wk.map((d) => <div key={iso(d)} style={{ background: color(d) }} className="h-[6px] w-[6px] rounded-[1px]" />)}
        </div>
      ))}
    </div>
  );
}

export function Stat({ label, value, sub, cls = "text-white" }) {
  return (
    <div className="rounded-xl bg-white/[0.04] border border-white/10 px-3 py-2.5">
      <div className="text-[9px] uppercase tracking-[0.2em] text-slate-400 font-semibold">{label}</div>
      <div className={`mt-1 font-display font-black text-lg leading-tight tracking-tight truncate ${cls}`}>{value ?? "—"}</div>
      {sub && <div className="text-[10px] text-slate-400 truncate">{sub}</div>}
    </div>
  );
}

export const tone = (n, mode) => (mode === "hide" ? "text-white" : n > 0 ? "text-emerald-400" : n < 0 ? "text-rose-400" : "text-white");

export const YearReviewCard = forwardRef(function YearReviewCard({ r, year, mode, name, t, lang }, ref) {
  const monthName = (m) => (m == null ? null : new Intl.DateTimeFormat(lang, { month: "long" }).format(new Date(year, Number(m), 1)));
  const dayName = (d) => (d == null ? null : new Intl.DateTimeFormat(lang, { weekday: "long" }).format(new Date(2024, 0, Number(d) === 0 ? 7 : Number(d))));
  const v = (n) => fmtValue(n, mode);
  return (
    <div ref={ref} data-testid="year-review-card" className="relative w-[540px] overflow-hidden rounded-3xl bg-slate-950 p-7 text-white">
      <div className="absolute -top-24 -right-20 h-72 w-72 rounded-full bg-amber-500/20 blur-3xl" />
      <div className="absolute -bottom-24 -left-20 h-72 w-72 rounded-full bg-blue-600/25 blur-3xl" />
      <div className="relative">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-amber-400 font-semibold">
          <Mountain size={14} /> Peak Market Society
        </div>
        <div className="mt-3 font-display font-black text-4xl tracking-tight leading-none">{t("insights.review.cardTitle", { year })}</div>
        {name && <div className="mt-1.5 text-sm text-slate-300">{name}</div>}

        <div className="mt-5 flex items-end gap-4">
          {mode !== "hide" && (
            <div data-testid="year-review-total" className={`font-display font-black text-5xl tracking-tight leading-none ${tone(r.total, mode)}`}>{v(r.total)}</div>
          )}
          <div className="pb-1 text-xs text-slate-400 leading-snug">
            {t("insights.review.tradesLine", { count: r.trades })}<br />{t("insights.review.winRateLine", { wr: r.winRate })}
          </div>
        </div>

        <div className="mt-5 rounded-2xl bg-white/[0.03] border border-white/10 p-3"><MiniHeat dayMap={r.dayMap} year={year} /></div>

        <div className="mt-4 grid grid-cols-3 gap-2">
          <Stat label={t("insights.review.greenDays")} value={`${r.greenDays}/${r.tradingDays}`} />
          <Stat label={t("insights.review.streak")} value={r.streak} sub={t("insights.review.streakSub")} />
          <Stat label={t("insights.review.topInstrument")} value={r.topInstrument?.k} sub={r.topInstrument && t("insights.review.tradesLine", { count: r.topInstrument.n })} />
          <Stat label={t("insights.review.bestMonth")} value={monthName(r.bestMonth?.k)} sub={r.bestMonth && v(r.bestMonth.v)} cls="text-emerald-400 capitalize" />
          <Stat label={t("insights.review.bestDay")} value={dayName(r.bestWeekday?.k)} sub={r.bestWeekday && v(r.bestWeekday.v)} cls="text-white capitalize" />
          <Stat label={t("insights.review.bestSession")} value={r.bestSession ? t(`sessions.${r.bestSession.k}`) : null} sub={r.bestSession && v(r.bestSession.v)} />
          <Stat label={t("insights.review.avgGrade")} value={r.topGrade} sub={r.graded ? t("insights.review.gradedSub", { count: r.graded, score: r.avgScore }) : null} cls="text-amber-400" />
          <Stat label={t("insights.review.climb")} value={r.climbTrades} sub={t("insights.review.climbSub", { count: r.plans })} />
          <Stat label={t("insights.review.badges")} value={r.badges.length} sub={t("insights.review.badgesSub")} cls="text-amber-400" />
        </div>
        <div className="mt-5 text-[10px] text-slate-500">{t("insights.review.footer")}</div>
      </div>
    </div>
  );
});
